import React, { useState } from 'react';
import {
  PiCaretDownLight,
  PiCaretRightLight,
  PiImageLight,
  PiImageSquareLight,
  PiPaletteLight,
  PiTextAaLight,
} from 'react-icons/pi';
import type { WebsiteUpsertRequest } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { SectionHeading } from 'src/modules/shared/components/molecules/section-heading/section-heading';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { isWebsiteContactHttpUrlValid } from '../../utils/website-contact-validation.util';
import { WebsiteBrandColorInput } from '../molecules/website-brand-color-input';
import { WebsiteLogoInput } from '../molecules/website-logo-input';
import { WebsiteThemeColorPicker } from '../molecules/website-theme-color-picker';

/** Headline length that still fits the home page of every design. */
const HEADLINE_RECOMMENDED_LENGTH = 60;

/** Theme colors the agency can edit. */
type WebsiteThemeColorKey = 'primaryColor' | 'secondaryColor' | 'accentColor';

/** Content fields edited in the brand step. */
type WebsiteBrandContent = Pick<
  WebsiteUpsertRequest['draftConfig']['content'],
  'siteName' | 'headline' | 'description'
>;

/** Logo upload state and actions owned by the page. */
interface WebsiteBrandStepLogo {
  /** Stable URL of the selected logo. */
  logoUrl?: string;
  /** Whether the selected design ignores logos. */
  inactive: boolean;
  /** Whether an upload, save, or publish is running. */
  busy: boolean;
  /** Upload progress from zero through one hundred. */
  progress: number;
  /** Polite status announcement for the current operation. */
  statusMessage?: string;
  /** Upload or validation error announcement. */
  errorMessage?: string;
  /** Whether the published logo cleanup waits for the next publication. */
  showDeferredCleanup: boolean;
  /** Called with the file the user picks. */
  onFileSelected: (file: File) => void;
  /** Removes the logo from the draft. */
  onRemove: () => void;
}

/** Props for the brand step. */
interface WebsiteBrandStepProps {
  /** Website draft currently in the editor. */
  form: WebsiteUpsertRequest;
  /** Whether the current user may not edit the website. */
  disabled: boolean;
  /** Logo upload state and actions. */
  logo: WebsiteBrandStepLogo;
  /** Called when a theme color changes. */
  onThemeColorChange: (key: WebsiteThemeColorKey, value: string) => void;
  /** Called when the name, headline, or description changes. */
  onContentChange: (content: Partial<WebsiteBrandContent>) => void;
  /** Called when the cover image link changes. */
  onHeroImageUrlChange: (heroImageUrl: string) => void;
}

/**
 * Brand step: logo, brand color with optional secondary colors, site name,
 * headline with a length hint, short description, and cover image link.
 *
 * @param props - Draft, logo state, and change callbacks.
 * @returns The brand step panel.
 */
export const WebsiteBrandStep: React.FC<WebsiteBrandStepProps> = ({
  form,
  disabled,
  logo,
  onThemeColorChange,
  onContentChange,
  onHeroImageUrlChange,
}) => {
  const { t } = useI18n();
  const content = form.draftConfig.content;
  const theme = form.draftConfig.theme;
  const headlineLength = (content.headline ?? '').length;
  const headlineTooLong = headlineLength > HEADLINE_RECOMMENDED_LENGTH;
  const heroImageUrlValid = isWebsiteContactHttpUrlValid(theme?.heroImageUrl);
  const [showSecondaryColors, setShowSecondaryColors] = useState(false);

  return (
    <section className="space-y-4">
      <SectionHeading
        title={t('websites.settings.brand.title')}
        description={t('websites.settings.brand.description')}
      />
      <InlineFormSection
        title={t('websites.settings.logo.label')}
        description={t('websites.settings.logo.help')}
        icon={<PiImageLight className="h-5 w-5" aria-hidden />}
      >
        <WebsiteLogoInput
          embedded
          label={t('websites.settings.logo.label')}
          helpText={t('websites.settings.logo.help')}
          inactiveText={t('websites.settings.logo.inactive')}
          removeLabel={t('websites.settings.logo.remove')}
          deferredCleanupText={t('websites.settings.logo.deferredCleanup')}
          previewAlt={t('websites.settings.logo.previewAlt')}
          progressLabel={t('websites.settings.logo.progressLabel')}
          logoUrl={logo.logoUrl}
          disabled={disabled}
          inactive={logo.inactive}
          busy={logo.busy}
          progress={logo.progress}
          statusMessage={logo.statusMessage}
          errorMessage={logo.errorMessage}
          showDeferredCleanup={logo.showDeferredCleanup}
          onFileSelected={logo.onFileSelected}
          onRemove={logo.onRemove}
        />
      </InlineFormSection>
      <InlineFormSection
        title={t('websites.settings.brand.color.title')}
        description={t('websites.settings.brand.color.description')}
        icon={<PiPaletteLight className="h-5 w-5" aria-hidden />}
      >
        <WebsiteBrandColorInput
          labelVisuallyHidden
          label={t('websites.settings.brand.color.title')}
          value={theme?.primaryColor ?? ''}
          disabled={disabled}
          onChange={(value) => onThemeColorChange('primaryColor', value)}
        />
        <div className="space-y-3">
          <Button
            size="sm"
            variant="neutral"
            appearance="ghost"
            aria-expanded={showSecondaryColors}
            onClick={() => setShowSecondaryColors((current) => !current)}
          >
            {showSecondaryColors ? (
              <PiCaretDownLight className="h-4 w-4" aria-hidden />
            ) : (
              <PiCaretRightLight className="h-4 w-4" aria-hidden />
            )}
            {t('websites.settings.brand.secondaryColors.title')}
          </Button>
          {showSecondaryColors ? (
            <div className="space-y-3">
              <Text size="small" color="secondary">
                {t('websites.settings.brand.secondaryColors.description')}
              </Text>
              <div className="grid gap-4 md:grid-cols-2">
                <WebsiteThemeColorPicker
                  label={t('websites.settings.brand.secondaryColor.label')}
                  value={theme?.secondaryColor ?? ''}
                  invalidText={t('websites.settings.brand.color.invalid')}
                  onChange={(value) =>
                    onThemeColorChange('secondaryColor', value)
                  }
                  disabled={disabled}
                />
                <WebsiteThemeColorPicker
                  label={t('websites.settings.brand.accentColor.label')}
                  value={theme?.accentColor ?? ''}
                  invalidText={t('websites.settings.brand.color.invalid')}
                  onChange={(value) => onThemeColorChange('accentColor', value)}
                  disabled={disabled}
                />
              </div>
            </div>
          ) : null}
        </div>
      </InlineFormSection>
      <InlineFormSection
        title={t('websites.settings.brand.texts.title')}
        description={t('websites.settings.brand.texts.description')}
        icon={<PiTextAaLight className="h-5 w-5" aria-hidden />}
      >
        <LabelTextInput
          label={t('websites.settings.brand.siteName.label')}
          labelPosition="top"
          inputProps={{
            'aria-label': t('websites.settings.brand.siteName.label'),
            value: content.siteName,
            disabled,
            onChange: (event) =>
              onContentChange({ siteName: event.target.value }),
          }}
        />
        <div className="space-y-1.5">
          <LabelTextInput
            label={t('websites.settings.brand.headline.label')}
            labelPosition="top"
            inputProps={{
              'aria-label': t('websites.settings.brand.headline.label'),
              value: content.headline ?? '',
              disabled,
              onChange: (event) =>
                onContentChange({ headline: event.target.value }),
            }}
          />
          <div className="flex flex-wrap justify-between gap-2">
            <Text size="tiny" color={headlineTooLong ? 'warning' : 'secondary'}>
              {headlineTooLong
                ? t('websites.settings.brand.headline.tooLong', {
                    max: HEADLINE_RECOMMENDED_LENGTH,
                  })
                : t('websites.settings.brand.headline.help')}
            </Text>
            <Text size="tiny" color={headlineTooLong ? 'warning' : 'secondary'}>
              {t('websites.settings.brand.headline.count', {
                count: headlineLength,
                max: HEADLINE_RECOMMENDED_LENGTH,
              })}
            </Text>
          </div>
        </div>
        <LabelTextArea
          label={t('websites.settings.brand.description.label')}
          labelPosition="top"
          inputProps={{
            rows: 3,
            'aria-label': t('websites.settings.brand.description.label'),
            value: content.description ?? '',
            disabled,
            onChange: (event) =>
              onContentChange({ description: event.target.value }),
          }}
        />
      </InlineFormSection>
      <InlineFormSection
        title={`${t('websites.settings.brand.heroImage.title')} ${t('websites.settings.brand.heroImage.optional')}`}
        description={t('websites.settings.brand.heroImage.help')}
        icon={<PiImageSquareLight className="h-5 w-5" aria-hidden />}
      >
        <LabelTextInput
          label={t('websites.settings.brand.heroImage.label')}
          labelPosition="top"
          inputProps={{
            'aria-label': t('websites.settings.brand.heroImage.label'),
            type: 'url',
            placeholder: 'https://',
            value: theme?.heroImageUrl ?? '',
            error: heroImageUrlValid
              ? undefined
              : t('websites.settings.contact.validation.socialUrl'),
            'aria-invalid': !heroImageUrlValid,
            disabled,
            autoComplete: 'url',
            onChange: (event) => onHeroImageUrlChange(event.target.value),
          }}
        />
      </InlineFormSection>
    </section>
  );
};
